<script setup>
import DepartmentDropdown from "@/components/DepartmentDropdown.vue";
import { BIconX } from "bootstrap-icons-vue";

import {
  trainingStatusesDictionary,
  trainingStatusesColorsDictionary,
  TrainingStatusEnum,
} from "@/constants/trainings.js";

import { useUserStore } from "@/stores/user";
import { useDepartmentStore } from "@/stores/department";
import { getInitials, getUserNameAndFallbacksFromRawData } from "@/utils/utils.js";
import { TRAINING_NOTE } from "@/constants/verbiage.js";

import { useInteractionTrainingStore } from "@/stores/interactionTraining";

// props
const props = defineProps({
  interactionId: {
    type: String,
    required: true,
  },
});

const trainingStore = useInteractionTrainingStore();
const userStore = useUserStore();
const departmentStore = useDepartmentStore();

const isExpanded = ref(true);
const loading = ref(false);

// ===========================
// GET REFS FROM STORE
// ===========================
const interaction = computed(() => trainingStore.activeInteraction);
const training = computed(() => trainingStore.activeTraining);

// Guard for bad loads
const trainingExists = computed(() => !!training.value);
const interactionExists = computed(() => !!interaction.value);

// ===========================
// COMPUTED PROPERTIES
// ===========================
const trainingApproverIds = computed(() => {
  const deptId = training.value?.department;
  return (
    departmentStore.allDict[deptId]?.training_approvers_acc_ids || []
  );
});

const isUserApprover = computed(() => {
  return trainingApproverIds.value.includes(userStore.authUserId);
});

const isTrainingEditable = computed(() => {
  if (!trainingExists.value) return false;

  const status = training.value.Status;

  if ([TrainingStatusEnum.LIVE, TrainingStatusEnum.REJECTED].includes(status)) {
    return false;
  }

  return isUserApprover.value || status === TrainingStatusEnum.REVIEW;
});

const isTrainingButtonsVisible = computed(() => {
  const status = training.value?.Status;
  return (
    isUserApprover.value ||
    status === TrainingStatusEnum.REVIEW
  );
});

const trainingApproversAvatarList = computed(() => {
  if (!trainingExists.value) return [];

  const approvers = training.value.final_approver
    ? [training.value.final_approver]
    : trainingApproverIds.value;

  return approvers.map((id, index) => ({
    shortName: getInitials(getUserNameAndFallbacksFromRawData(userStore.allDict[id])),
    tooltip:
      index < 3
        ? `<span>${getUserNameAndFallbacksFromRawData(userStore.allDict[id])}</span><br/><span>Training Approver</span>`
        : `${getUserNameAndFallbacksFromRawData(userStore.allDict[id])}`,
  }));
});

const trainingCreatorName = computed(() => {
  if (!trainingExists.value) return "";
  return getUserNameAndFallbacksFromRawData(
    userStore.allDict[training.value.user_id_pv2]
  );
});

const trainingCreatorTooltip = computed(() => {
  return `<span>${trainingCreatorName.value}</span><br/><span>Training Creator</span>`;
});

// ===========================
// ACTIONS
// ===========================
const updateDepartment = (newDept) => {
  if (trainingExists.value) {
    training.value.department = newDept;
  }
};

const onClose = () => {
  trainingStore.isTrainingPopupOpen = false;
};

const switchStatus = async (newStatus) => {
  if (!trainingExists.value) return;

  loading.value = true;
  const prev = training.value.Status;

  try {
    training.value.Status = newStatus;
    await trainingStore.updateTraining();
  } catch (err) {
    training.value.Status = prev;
    console.error(err);
  } finally {
    loading.value = false;
  }
};

const onSaveDraft = () => switchStatus(TrainingStatusEnum.REVIEW);
const onDiscard = async () => {
  loading.value = true;
  try {
    training.value.Status = TrainingStatusEnum.ATTEMPT;
    await trainingStore.updateTraining();
    trainingStore.resetTraining();
    onClose();
  } finally {
    loading.value = false;
  }
};
const onApprove = () => switchStatus(TrainingStatusEnum.LIVE);
const onSendForApproval = () => switchStatus(TrainingStatusEnum.PENDING);
const onReject = () => switchStatus(TrainingStatusEnum.REJECTED);

// Load training for this interaction ID
onMounted(async () => {
  await trainingStore.load(props.interactionId);
});
</script>

<template>
  <div class="training-dialog position-absolute bottom-0" v-if="trainingExists && interactionExists">
    <list-loader :show="loading" />

    <!-- HEADER -->
    <div class="header cursor-pointer d-flex align-items-center column-gap-3" @click="isExpanded = !isExpanded">

      <user-avatar
        class="user-avatar"
        :shortName="getInitials(trainingCreatorName)"
        icon-half-rounded
        :tooltip="trainingCreatorTooltip"
        icon-position="top-right"
        size="medium"
        theme="neutral"
      />

      <div class="d-flex ms-4 justify-content-between align-items-center flex-grow-1">
        <div>
          <div class="interaction-subtitle text-neutral-600 font-size-sm">
            <span class="text-decoration-underline">#{{ interaction.short_id }}</span> -
            <span>{{ interaction._createdAt_formatted_full_str }}</span>
          </div>

          <div class="mt-2 d-flex align-items-center column-gap-2">
            <status-badge
              :theme="trainingStatusesColorsDictionary[training.Status]"
              :value="training.Status"
              :label="trainingStatusesDictionary[training.Status]"
            />

            <DepartmentDropdown
              class="w-fit-content"
              :disabled="!isTrainingEditable"
              :dptId="training.department"
              :organizationId="interaction.company_id"
              @updated="updateDepartment"
            />
          </div>
        </div>

        <UserAvatarsList
          class="assignees-readonly-wrapper"
          :list="trainingApproversAvatarList"
          :limit="3"
          size="medium"
          theme="neutral"
        />

      </div>

      <BIconX @click.stop.prevent="onClose" width="25" height="25" style="color: rgb(114, 133, 153)" />
    </div>

    <!-- BODY -->
    <b-collapse id="training-dialog-collapse" v-model="isExpanded">
      <div class="dialog-body">
        <div>
          <p class="font-size-sm text-neutral-400 fw-semibold mt-2">How to Train</p>
          <span class="font-size-sm mt-2 text-neutral-400" v-html="TRAINING_NOTE" />
        </div>

        <div>
          <p class="font-size-sm text-neutral-400 fw-semibold mt-2">Query</p>

          <textarea-input
            v-if="isTrainingEditable"
            class="mt-2"
            autoHeight
            v-model="training.updated_query"
          />

          <div v-else class="font-size-sm mt-2 text-neutral-400 bordered-query">
            {{ training.updated_query }}
          </div>
        </div>

        <div>
          <p class="font-size-sm text-neutral-400 fw-semibold mt-2">Training Response</p>

          <textarea-input
            v-if="isTrainingEditable"
            class="mt-2"
            autoHeight
            v-model="training.Data"
          />

          <div v-else class="font-size-sm mt-2 text-neutral-400 bordered-query">
            {{ training.Data }}
          </div>
        </div>
      </div>

      <!-- FOOTER BUTTONS -->
      <div class="footer d-flex align-items-center"
        :class="{
          'justify-content-center': !isTrainingButtonsVisible,
          'justify-content-between': isTrainingButtonsVisible,
        }"
      >

        <!-- REVIEW MODE -->
        <template v-if="training.Status === TrainingStatusEnum.REVIEW">
          <span class="text-neutral-400 font-size-xs fw-semibold cursor-pointer" @click="onSaveDraft">
            Save Draft
          </span>

          <div class="d-flex align-items-center column-gap-2">
            <custom-button button-type="white" has-font-bold @click="onDiscard">
              Discard Training
            </custom-button>

            <custom-button
              v-if="isUserApprover"
              button-type="blue"
              has-font-bold
              @click="onApprove"
            >
              Approve
            </custom-button>

            <custom-button
              v-else
              button-type="blue"
              has-font-bold
              @click="onSendForApproval"
            >
              Send for Approval
            </custom-button>
          </div>
        </template>

        <!-- PENDING -->
        <template v-if="training.Status === TrainingStatusEnum.PENDING">
          <template v-if="isUserApprover">
            <custom-button button-type="white" has-font-bold @click="onReject">
              Reject
            </custom-button>
            <custom-button button-type="blue" has-font-bold @click="onApprove">
              Approve
            </custom-button>
          </template>

          <template v-else>
            <span class="text-neutral-600">Training is being reviewed</span>
          </template>
        </template>

        <!-- LIVE -->
        <template v-if="training.Status === TrainingStatusEnum.LIVE">
          <template v-if="isUserApprover">
            <custom-button button-type="white" has-font-bold @click="onReject">
              Reject
            </custom-button>
          </template>

          <template v-else>
            <span class="text-neutral-600">Training is live</span>
          </template>
        </template>

      </div>
    </b-collapse>
  </div>
</template>

<style scoped lang="scss">
:deep {
  .custom-textarea {
    color: $neutral-400;
  }

  textarea {
    font-size: 14px;
  }
}

.training-dialog {
  background-color: white;
  z-index: 999;
  width: 750px;
  box-shadow: -4px -4px 35px 0px rgba(0, 0, 0, 0.13);
  right: 30px;
  border-radius: 6px 6px 0 0;
  max-width: 600px;
  max-height: 470px;

  .bordered-query {
    padding: 12px;
    border-radius: 12px;
    border: 1px solid $neutral-150;
    word-break: break-all;
  }

  :deep(.status-badge) {
    background: $bluish-gray-100;
  }

  :deep(.icon-marker) {
    svg {
      fill: $neutral-300;
    }
  }

  .header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid $bluish-gray-400;
    background: $neutral-50;
    border-radius: 12px 12px 0px 0px;
  }

  .dialog-body {
    padding: 1.5rem;
    overflow: auto;
    height: 290px;
    > div {
      &:nth-child(n + 2) {
        margin-top: 2.25rem;
      }
    }
  }

  .footer {
    padding: 1.5rem;
    background-color: $neutral-50;
    border-top: 1px solid $bluish-gray-400;
  }

  .assignees-readonly-wrapper {
    .assignee-readonly {
      :deep(.avatar-wrapper) {
        .icon-marker-icon {
          background: $bluish-gray-700;
        }
      }
    }
  }
}
</style>
